import { useState, useEffect, useRef } from "react";
import { useSignUp, useClerk } from "@clerk/react";

const STYLES = `
  @font-face {
    font-family: "Uncut Sans";
    src: url("https://cdn.prod.website-files.com/692d376203e424bd8a70a3cf/692d376203e424bd8a70a3e5_UncutSans-Variable.ttf") format("truetype");
    font-weight: 300 700;
    font-style: normal;
    font-display: swap;
  }
  .ai-page {
    font-family: "Uncut Sans", sans-serif !important;
    -webkit-font-smoothing: antialiased;
    min-height: 100vh;
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin: 0; padding: 0;
  }
  .ai-panel {
    display: flex; flex-direction: column;
    padding: 60px 72px; box-sizing: border-box; min-height: 100vh;
  }
  .ai-logo { width: 280px; height: auto; display: block; margin-bottom: 56px; }
  .ai-heading {
    font-size: 48px; font-weight: 500; line-height: 1em;
    letter-spacing: -0.02em; margin: 0 0 40px; color: #000;
    font-family: "Uncut Sans", sans-serif !important;
  }
  .ai-fields { display: flex; flex-direction: column; gap: 16px; flex: 1; }
  .ai-label {
    display: block; font-size: 16px; font-weight: 500; color: #000;
    margin-bottom: 6px; font-family: "Uncut Sans", sans-serif !important;
  }
  .ai-input {
    background: #0000000d; color: #000; border: 1px solid transparent;
    border-radius: 8px; height: 54px; padding: 0 18px; font-size: 18px;
    font-family: "Uncut Sans", sans-serif !important; box-sizing: border-box;
    width: 100%; outline: none; transition: border-color 0.2s;
  }
  .ai-input:focus { border-color: #000; }
  .ai-btn {
    border: 1px solid #000; background: #000; color: #fff;
    border-radius: 200px; height: 46px; padding: 0 36px;
    font-weight: 500; font-size: 16px; font-family: "Uncut Sans", sans-serif !important;
    cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
    align-self: flex-start; transition: background-color 0.2s, color 0.2s;
  }
  .ai-btn:hover:not(:disabled) { background: #fff; color: #000; }
  .ai-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .ai-error { color: #cc0000; font-size: 0.875em; margin: 0; font-family: "Uncut Sans", sans-serif !important; }
  .ai-sub { font-size: 15px; color: rgba(0,0,0,0.55); margin: 0 0 8px; font-family: "Uncut Sans", sans-serif !important; }
  .ai-terms-label {
    display: flex; align-items: flex-start; gap: 10px; cursor: pointer;
    font-size: 14px; color: rgba(0,0,0,0.7); line-height: 1.5;
    font-family: "Uncut Sans", sans-serif !important;
  }
  .ai-terms-label a { color: #000; }
  .ai-terms-check { margin-top: 3px; flex-shrink: 0; width: 16px; height: 16px; cursor: pointer; }
  .ai-img-panel { position: relative; overflow: hidden; min-height: 100vh; }
  .ai-img-panel img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
  @media (max-width: 767px) {
    .ai-page { grid-template-columns: 1fr; }
    .ai-img-panel { display: none; }
    .ai-panel { padding: 48px 32px; }
    .ai-heading { font-size: 36px; }
    .ai-logo { width: 180px; margin-bottom: 40px; }
  }
`;

export default function AcceptInvitation() {
  const { signUp } = useSignUp();
  const { setActive } = useClerk();
  const processed = useRef(false);

  // Read ticket before Clerk can strip it from the URL
  const [ticket] = useState(() => new URLSearchParams(window.location.search).get("__clerk_ticket"));

  const [step,          setStep]          = useState("loading");
  const [firstName,     setFirstName]     = useState("");
  const [lastName,      setLastName]      = useState("");
  const [password,      setPassword]      = useState("");
  const [confirmPw,     setConfirmPw]     = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error,         setError]         = useState("");
  const [loading,       setLoading]       = useState(false);

  useEffect(() => {
    const el = document.createElement("style");
    el.setAttribute("data-ai", "");
    el.textContent = STYLES;
    document.head.appendChild(el);
    return () => { try { document.head.removeChild(el); } catch (_) {} };
  }, []);

  // Show form once Clerk is ready — keep __clerk_ticket in URL so signUp.ticket() can read it
  useEffect(() => {
    if (!signUp || processed.current) return;
    processed.current = true;

    // Use the ticket captured at mount — Clerk may have already stripped it from the URL
    if (!ticket) {
      setStep("error");
      setError("No invitation token found. Please use the link from your invitation email.");
      return;
    }

    const fn = signUp.firstName || signUp.unsafeMetadata?.first_name || signUp.publicMetadata?.first_name || "";
    const ln = signUp.lastName  || signUp.unsafeMetadata?.last_name  || signUp.publicMetadata?.last_name  || "";
    if (fn) setFirstName(fn);
    if (ln) setLastName(ln);

    setStep("set-password");
  }, [signUp]);

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (password !== confirmPw) { setError("Passwords do not match."); return; }
    if (password.length < 8)    { setError("Password must be at least 8 characters."); return; }
    setError("");
    setLoading(true);
    try {
      // create() with strategy:"ticket" mutates signUp in-place — check signUp.status after
      await signUp.create({
        strategy:      "ticket",
        ticket,
        password,
        firstName:     firstName.trim() || undefined,
        lastName:      lastName.trim()  || undefined,
        legalAccepted: true,
      });

      if (signUp.status === "complete" && signUp.createdSessionId) {
        window.history.replaceState(null, "", "/accept-invitation");
        await setActive({ session: signUp.createdSessionId });
        window.location.href = "/";
        return;
      } else if (signUp.status === "missing_requirements") {
        const missing = signUp.missingFields || [];
        if (missing.includes("email_address")) {
          setError("Please verify your email address — check your inbox for a verification email.");
        } else {
          setError("Could not complete account setup. Please try again or contact support.");
        }
      } else {
        setError("Could not complete account setup. Please try again or contact support.");
      }
    } catch (err) {
      console.error("[AI] caught error:", err);
      console.error("[AI] clerk errors:", err?.errors);
      const clerkErr = err?.errors?.[0];
      const code     = clerkErr?.code || "";
      const msg      = (clerkErr?.message || "").toLowerCase();

      // Ticket already used or account already exists — guide them to sign in
      if (
        code === "form_identifier_exists" ||
        code === "resource_already_exists" ||
        code === "ticket_invalid" ||
        code === "invitation_already_accepted" ||
        msg.includes("already") ||
        msg.includes("taken")
      ) {
        setStep("already-exists");
      } else {
        setError(
          clerkErr?.longMessage ||
          clerkErr?.message ||
          "Failed to create account. Please try again or contact support."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ai-page">
      <div className="ai-panel">
        <img src="/logo-blue.png" alt="InSpekt" className="ai-logo" />

        {step === "loading" && (
          <div className="ai-fields">
            <p className="ai-sub">Verifying your invitation…</p>
          </div>
        )}

        {step === "error" && (
          <div className="ai-fields">
            <h1 className="ai-heading">Invitation error</h1>
            <p className="ai-error">{error}</p>
            <a href="/" className="ai-btn" style={{ marginTop: 8, textDecoration: "none" }}>
              Back to login
            </a>
          </div>
        )}

        {step === "already-exists" && (
          <div className="ai-fields">
            <h1 className="ai-heading">Account already exists</h1>
            <p className="ai-sub">
              It looks like an account for this email has already been created —
              either you signed up before or this invitation link was already used.
            </p>
            <a href="/" className="ai-btn" style={{ marginTop: 8, textDecoration: "none" }}>
              Sign in instead
            </a>
          </div>
        )}

        {step === "set-password" && (
          <form onSubmit={handleSetPassword}>
            <h1 className="ai-heading">
              {firstName ? `Hi ${firstName},` : "Hi,"}
            </h1>
            <p className="ai-sub">Complete your account setup by choosing a password.</p>
            <div className="ai-fields">
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label className="ai-label">First name</label>
                  <input type="text" required autoFocus className="ai-input"
                    value={firstName} onChange={e => setFirstName(e.target.value)}
                    autoComplete="given-name" />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="ai-label">Last name</label>
                  <input type="text" required className="ai-input"
                    value={lastName} onChange={e => setLastName(e.target.value)}
                    autoComplete="family-name" />
                </div>
              </div>
              <div>
                <label className="ai-label">Password</label>
                <input type="password" minLength="8" required className="ai-input"
                  value={password} onChange={e => setPassword(e.target.value)}
                  autoComplete="new-password" />
              </div>
              <div>
                <label className="ai-label">Confirm password</label>
                <input type="password" minLength="8" required className="ai-input"
                  value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
                  autoComplete="new-password" />
              </div>
              <label className="ai-terms-label">
                <input type="checkbox" className="ai-terms-check"
                  checked={termsAccepted}
                  onChange={e => setTermsAccepted(e.target.checked)} />
                By creating an account, I agree to this website's{" "}
                <a href="https://www.ratekon.com/company/terms" target="_blank" rel="noopener noreferrer">
                  terms of service
                </a>
              </label>
              {error && <p className="ai-error">{error}</p>}
              <button type="submit" className="ai-btn" disabled={loading || !termsAccepted}>
                {loading ? "Please wait…" : "Create account"}
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="ai-img-panel">
        <img src="/login-bg.webp" alt="" />
      </div>
    </div>
  );
}

